import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SyllabusEditor } from "@/components/SyllabusEditor";
import { toast } from "sonner";
import { UFS } from "@/lib/ufs";
import { formatCourseDate } from "@/lib/dates";

// Sentinela para "Padrão do sistema" no Select de modelo (Select do shadcn não
// aceita value=""). Convertida para null ao enviar/ler.
const DEFAULT_MODEL = "__default__";

const empty = {
  title: "", description: "", area: "legislativo", professor: "", cidade: "", estado: "",
  start_date: "", end_date: "", workload_hours: 16, content_workload_hours: "",
  certificate_model_id: DEFAULT_MODEL,
  modality: "presencial", min_attendance_percent: 75,
  syllabus_modules: [], objectives: "",
};

export default function AdminCursosPresenciais() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [certModels, setCertModels] = useState([]);

  const load = () => api.get("/courses").then(r => setItems(r.data || [])).catch(() => {});
  const loadModels = () => api.get("/admin/certificate-models").then(r => setCertModels(r.data || [])).catch(() => {});
  useEffect(() => { load(); loadModels(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    // Validação manual de Estado (Select não tem atributo `required` nativo)
    if (!form.estado) {
      toast.error("Selecione o Estado (UF) do curso");
      return;
    }
    // Item 2 (Briefing out/2026): modelo de certificado é OBRIGATÓRIO ao CRIAR
    // um curso novo. Ao editar um curso legado, o campo permanece opcional.
    if (!editId && (!form.certificate_model_id || form.certificate_model_id === DEFAULT_MODEL)) {
      toast.error("Selecione um modelo de certificado para o curso.");
      return;
    }
    const payload = {
      ...form,
      workload_hours: Number(form.workload_hours),
      content_workload_hours:
        form.content_workload_hours === "" || form.content_workload_hours == null
          ? null
          : Number(form.content_workload_hours),
      certificate_model_id:
        form.certificate_model_id && form.certificate_model_id !== DEFAULT_MODEL
          ? form.certificate_model_id
          : null,
      min_attendance_percent: Number(form.min_attendance_percent),
      // ago/2026 — enviar dia-calendário como YYYY-MM-DD (sem timezone).
      // O `new Date(str).toISOString()` antes usado converte para UTC e
      // causava o clássico "1 dia a menos" ao renderizar em pt-BR (UTC-3).
      // Ver /app/frontend/src/lib/dates.js para detalhes.
      start_date: (form.start_date || "").slice(0, 10),
      end_date: (form.end_date || "").slice(0, 10),
      syllabus_modules: (form.syllabus_modules || [])
        .filter((m) => (m.title || "").trim().length > 0)
        .map((m) => ({
          title: (m.title || "").trim(),
          description: (m.description || "").trim() || null,
          topics: (m.topics || []).map((t) => String(t).trim()).filter(Boolean),
          hours: m.hours === "" || m.hours == null ? null : Number(m.hours),
        })),
      objectives: (form.objectives || "").trim(),
    };
    try {
      if (editId) await api.put(`/courses/${editId}`, payload);
      else await api.post("/courses", payload);
      toast.success("Salvo!");
      setOpen(false); setEditId(null); setForm(empty); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const edit = (c) => {
    setEditId(c.id);
    setForm({
      title: c.title, description: c.description || "", area: c.area, professor: c.professor,
      cidade: c.cidade, estado: c.estado,
      start_date: c.start_date?.slice(0, 10), end_date: c.end_date?.slice(0, 10),
      workload_hours: c.workload_hours,
      content_workload_hours: c.content_workload_hours ?? "",
      certificate_model_id: c.certificate_model_id || DEFAULT_MODEL,
      modality: c.modality, min_attendance_percent: c.min_attendance_percent,
      syllabus_modules: Array.isArray(c.syllabus_modules) ? c.syllabus_modules : [],
      objectives: c.objectives || "",
    });
    setOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Inativar este curso?")) return;
    await api.delete(`/courses/${id}`);
    toast.success("Curso inativado");
    load();
  };

  return (
    <AdminLayout title="Cursos Presenciais">
      <div className="flex justify-end mb-3">
        <Button onClick={() => { setEditId(null); setForm(empty); setOpen(true); }} data-testid="course-new"><Plus className="w-4 h-4 mr-1" /> Novo curso</Button>
      </div>
      <Card className="rounded-xl">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Curso</TableHead><TableHead>Área</TableHead><TableHead>Local</TableHead>
              <TableHead>Datas</TableHead><TableHead>CH</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map(c => {
                return (
                  <TableRow key={c.id}>
                    <TableCell><p className="font-medium">{c.title}</p><p className="text-xs text-muted-foreground">{c.professor}</p></TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{c.area}</Badge></TableCell>
                    <TableCell>{c.cidade}/{c.estado}</TableCell>
                    <TableCell>{formatCourseDate(c.start_date)} – {formatCourseDate(c.end_date)}</TableCell>
                    <TableCell>{c.workload_hours}h</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => edit(c)} data-testid={`course-edit-${c.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(c.id)} data-testid={`course-delete-${c.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">Nenhum curso cadastrado</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[720px] rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Editar curso" : "Novo curso presencial"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label>Título</Label><Input required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="course-title" /></div>
            <div><Label>Descrição</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Área</Label>
                <Select value={form.area} onValueChange={(v) => setForm(f => ({ ...f, area: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="legislativo">Legislativo</SelectItem>
                    <SelectItem value="licitacoes">Licitações</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Modalidade</Label>
                <Select value={form.modality} onValueChange={(v) => setForm(f => ({ ...f, modality: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="presencial">Presencial</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="hibrido">Híbrido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Professor</Label><Input required value={form.professor} onChange={(e) => setForm(f => ({ ...f, professor: e.target.value }))} /></div>
              <div><Label>Carga Horária Total (h)</Label><Input type="number" required value={form.workload_hours} onChange={(e) => setForm(f => ({ ...f, workload_hours: e.target.value }))} data-testid="course-workload-total" /></div>
              <div><Label>Carga Horária de Conteúdo Programático (h)</Label><Input type="number" min={0} placeholder="Opcional" value={form.content_workload_hours} onChange={(e) => setForm(f => ({ ...f, content_workload_hours: e.target.value }))} data-testid="course-workload-content" /></div>
              <div><Label>Cidade</Label><Input required value={form.cidade} onChange={(e) => setForm(f => ({ ...f, cidade: e.target.value }))} /></div>
              <div>
                <Label>Estado (UF)</Label>
                <Select
                  value={form.estado}
                  onValueChange={(v) => setForm((f) => ({ ...f, estado: v }))}
                >
                  <SelectTrigger data-testid="course-estado-select">
                    <SelectValue placeholder="Selecione o estado" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {UFS.map((uf) => (
                      <SelectItem key={uf.value} value={uf.value} data-testid={`course-estado-option-${uf.value}`}>
                        {uf.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Início</Label><Input type="date" required value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} /></div>
              <div><Label>Fim</Label><Input type="date" required value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Frequência mínima (%)</Label>
                <Input type="number" min={0} max={100} value={form.min_attendance_percent} onChange={(e) => setForm(f => ({ ...f, min_attendance_percent: e.target.value }))} /></div>
              <div className="col-span-2">
                <Label>Modelo de certificado {!editId && <span className="text-rose-600">*</span>}</Label>
                <Select
                  value={form.certificate_model_id || DEFAULT_MODEL}
                  onValueChange={(v) => setForm((f) => ({ ...f, certificate_model_id: v }))}
                >
                  <SelectTrigger data-testid="course-cert-model-select">
                    <SelectValue placeholder="Selecione o modelo de certificado" />
                  </SelectTrigger>
                  <SelectContent>
                    {editId && (
                      <SelectItem value={DEFAULT_MODEL} data-testid="course-cert-model-default">Padrão do sistema (por área)</SelectItem>
                    )}
                    {certModels.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`course-cert-model-${m.id}`}>
                        {m.name}{m.has_front_image ? "" : " (sem imagem)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {editId
                    ? "Pode trocar o modelo deste curso. Certificados já emitidos mantêm o modelo original."
                    : "Obrigatório: o certificado deste curso usará este modelo (frente e verso)."}
                </p>
              </div>
            </div>

            <Separator className="my-2" />

            {/* Objetivos do curso (vai no verso, opcional) */}
            <div>
              <Label>Objetivos do curso <span className="text-muted-foreground text-xs">(opcional — sai no verso do certificado)</span></Label>
              <Textarea
                rows={2}
                value={form.objectives || ""}
                onChange={(e) => setForm(f => ({ ...f, objectives: e.target.value }))}
                placeholder="Ex: Capacitar vereadores, assessores e servidores no domínio do processo legislativo municipal..."
                data-testid="course-objectives"
              />
            </div>

            {/* Editor de Ementa em Módulos */}
            <SyllabusEditor
              value={form.syllabus_modules || []}
              onChange={(mods) => setForm(f => ({ ...f, syllabus_modules: mods }))}
            />

            <Button type="submit" className="w-full" data-testid="course-save">{editId ? "Salvar alterações" : "Cadastrar curso"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
